/**
 * LeagueStandingsPage — /league/:leagueId/standings
 * ─────────────────────────────────────────────────────────────────
 * Phase 6. The final user-facing surface for the league module.
 *
 * Two tabs:
 *   • Standings — per-division team standings driven by locked
 *     games. Columns: GP, W, L, T, OTL, GF, GA, GD, PTS.
 *     Sortable on every numeric column.
 *   • Leaderboard — player ranking by total points (G+A) across
 *     locked games this season. Shows G, A, PTS, PIM, GP.
 *
 * Below the tabs (collapsible disclosure): Scoring rules editor
 * for managers — points rubric (configurable per result type)
 * and tiebreakers ordering.
 *
 * Open question this page answers: "Who's winning?" — for both
 * the team race and the individual scoring race.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  Trophy, Award, Calendar, Save, ChevronDown, ChevronRight,
  Settings, Plus, RefreshCw, ArrowRight, ArrowUp, ArrowDown,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';

const TIEBREAKER_OPTIONS = [
  { v: 'points',            l: 'Points' },
  { v: 'wins',              l: 'Wins' },
  { v: 'goal_differential', l: 'Goal differential' },
  { v: 'goals_for',         l: 'Goals for' },
  { v: 'head_to_head',      l: 'Head-to-head' },
  { v: 'fewer_losses',      l: 'Fewer losses' },
  { v: 'games_played',      l: 'Fewer games played' },
];

const RUBRIC_FIELDS = [
  { k: 'win_reg',  l: 'Win (regulation)' },
  { k: 'win_ot',   l: 'Win (overtime)' },
  { k: 'win_so',   l: 'Win (shootout)' },
  { k: 'loss_ot',  l: 'Loss (overtime)' },
  { k: 'loss_so',  l: 'Loss (shootout)' },
  { k: 'tie',      l: 'Tie' },
  { k: 'loss_reg', l: 'Loss (regulation)' },
];

export default function LeagueStandingsPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canManageSettings = isManager || can('manage_league_settings');

  const [league, setLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonIdState] = useState('');
  // 2026-09-28 — ?season=<id> deep link; the picker keeps it in sync.
  const [searchParams, setSearchParams] = useSearchParams();
  const setSeasonId = (id) => {
    setSeasonIdState(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('season', id); else next.delete('season');
    setSearchParams(next, { replace: true });
  };
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('standings');

  const defaultSeasonId = useMemo(() => {
    if (seasons.length === 0) return '';
    const wanted = searchParams.get('season');
    if (wanted && seasons.some(s => s.id === wanted)) return wanted;
    const active = seasons.find(s => s.status === 'active');
    return active?.id || seasons[0].id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasons]);

  async function loadShell() {
    if (!leagueId) return;
    try {
      const [leagueRes, seasonsRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/seasons`),
      ]);
      setLeague(leagueRes?.data?.league || null);
      setSeasons(seasonsRes?.data?.seasons || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('You are not a member of this league');
      else if (err?.response?.status !== 401) toast.error('Could not load league');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadShell(); /* eslint-disable-next-line */ }, [leagueId]);
  useEffect(() => {
    if (!seasonId && defaultSeasonId) setSeasonIdState(defaultSeasonId);
  }, [defaultSeasonId, seasonId]);

  const currentSeason = seasons.find(s => s.id === seasonId) || null;

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or no access.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
      <LeagueSubNav league={league} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 className="section-title text-xl">Standings</h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
            Live standings + scoring leaders, computed from completed games.
          </p>
        </div>
      </div>

      {seasons.length === 0 ? (
        <EmptyNoSeasonsCard leagueId={leagueId} />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Calendar size={16} color="var(--ice)" />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Season:</span>
            <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)}
              style={{ ...inputStyle, width: 'auto', minWidth: 240, flex: '1 1 280px' }}>
              {seasons.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.status})
                </option>
              ))}
            </select>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 4, padding: 4,
            background: 'var(--surface-2)', borderRadius: 12,
            border: '1px solid var(--border)', marginBottom: 16, flexWrap: 'wrap',
          }}>
            <TabBtn active={tab === 'standings'}   onClick={() => setTab('standings')}   icon={Trophy}>Standings</TabBtn>
            <TabBtn active={tab === 'leaderboard'} onClick={() => setTab('leaderboard')} icon={Award}>Player leaders</TabBtn>
          </div>

          {seasonId && tab === 'standings' && (
            <StandingsTab leagueId={leagueId} seasonId={seasonId} />
          )}
          {seasonId && tab === 'leaderboard' && (
            <LeaderboardTab leagueId={leagueId} seasonId={seasonId} />
          )}

          {/* Scoring rules editor — only managers see */}
          {canManageSettings && seasonId && currentSeason && (
            <ScoringRulesEditor
              leagueId={leagueId} seasonId={seasonId}
              initialRubric={currentSeason.points_rubric_json}
              initialTiebreakers={currentSeason.tiebreakers_json}
              onSaved={loadShell}
            />
          )}
        </>
      )}
    </div>
  );
}

function EmptyNoSeasonsCard({ leagueId }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 36 }}>
      <Calendar size={36} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No seasons yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>
        Standings need a season. Create one first.
      </div>
      <Link to={`/league/${leagueId}/seasons`} className="btn-ice"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Plus size={14} />
        Go to Seasons
        <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '10px 16px', borderRadius: 8,
      fontSize: 13, fontWeight: 600,
      color: active ? 'var(--text)' : 'var(--text-2)',
      background: active ? 'var(--surface)' : 'transparent',
      border: 'none',
      boxShadow: active ? 'var(--shadow-sm)' : 'none',
      cursor: 'pointer',
      flex: '1 1 100px',
      justifyContent: 'center',
    }}>
      <Icon size={14} />
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// STANDINGS TAB
// ═══════════════════════════════════════════════════════════════

const STANDINGS_COLS = [
  // key,    label, align,  numeric?, sortable?
  { k: 'rank',              l: '#',   a: 'left',  numeric: false, sortable: false },
  { k: 'team_name',         l: 'Team', a: 'left', numeric: false, sortable: false },
  { k: 'games_played',      l: 'GP',  a: 'right', numeric: true,  sortable: true },
  { k: 'wins',              l: 'W',   a: 'right', numeric: true,  sortable: true },
  { k: 'losses',            l: 'L',   a: 'right', numeric: true,  sortable: true },
  { k: 'ot_losses',         l: 'OTL', a: 'right', numeric: true,  sortable: true },
  { k: 'ties',              l: 'T',   a: 'right', numeric: true,  sortable: true },
  { k: 'goals_for',         l: 'GF',  a: 'right', numeric: true,  sortable: true },
  { k: 'goals_against',     l: 'GA',  a: 'right', numeric: true,  sortable: true },
  { k: 'goal_differential', l: 'GD',  a: 'right', numeric: true,  sortable: true },
  { k: 'points',            l: 'PTS', a: 'right', numeric: true,  sortable: true, bold: true },
];

function StandingsTab({ leagueId, seasonId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);  // null = server's tiebreaker-driven order
  const [sortDir, setSortDir] = useState('desc');

  async function load() {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/leagues/${leagueId}/seasons/${seasonId}/standings`);
      setData(res);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load standings');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seasonId]);

  if (loading) return <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>;
  if (!data || data.divisions.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 36 }}>
        <Trophy size={36} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No teams in this season</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
          Add teams to the season and play some games — standings populate as games finalize.
        </div>
      </div>
    );
  }

  function clickSort(col) {
    if (!col.sortable) return;
    if (sortKey === col.k) {
      // Toggle direction, then off (back to server order).
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortKey(null); setSortDir('desc'); }
    } else {
      setSortKey(col.k);
      setSortDir('desc');
    }
  }

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12, flexWrap: 'wrap', gap: 8,
      }}>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
          {data.locked_games_count} completed game{data.locked_games_count === 1 ? '' : 's'} counted
          {sortKey && <> · custom sort active</>}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          {sortKey && (
            <button type="button" className="btn-ghost" style={tinyBtn}
              onClick={() => { setSortKey(null); setSortDir('desc'); }}>
              Reset sort
            </button>
          )}
          <button type="button" className="btn-ghost" style={tinyBtn} onClick={load}>
            <RefreshCw size={11} style={{ marginRight: 4 }} />
            Refresh
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {data.divisions.map(div => {
          const teams = sortKey
            ? div.teams.slice().sort((a, b) => {
                const x = a[sortKey] ?? 0; const y = b[sortKey] ?? 0;
                return sortDir === 'desc' ? y - x : x - y;
              })
            : div.teams;
          return (
            <div key={div.division_id || '__none'} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{
                padding: '12px 16px', borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
                fontSize: 13, fontWeight: 700,
              }}>
                {div.division_name || 'Unranked (no division)'}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  fontSize: 13, minWidth: 600,
                }}>
                  <thead>
                    <tr>
                      {STANDINGS_COLS.map(col => (
                        <th key={col.k} onClick={() => clickSort(col)} style={{
                          textAlign: col.a,
                          padding: '8px 10px',
                          borderBottom: '1px solid var(--border)',
                          fontSize: 10, color: 'var(--text-2)',
                          textTransform: 'uppercase', letterSpacing: 0.6,
                          fontWeight: 700,
                          cursor: col.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                        }}>
                          {col.l}
                          {col.sortable && sortKey === col.k && (
                            sortDir === 'desc'
                              ? <ArrowDown size={9} style={{ marginLeft: 3, verticalAlign: 'middle' }} />
                              : <ArrowUp   size={9} style={{ marginLeft: 3, verticalAlign: 'middle' }} />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((t, i) => (
                      <tr key={t.team_id} style={{
                        background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                      }}>
                        {STANDINGS_COLS.map(col => (
                          <td key={col.k} style={{
                            textAlign: col.a,
                            padding: '8px 10px',
                            borderBottom: i === teams.length - 1 ? 'none' : '1px solid var(--border)',
                            fontWeight: col.bold ? 700 : 500,
                            fontFamily: col.numeric ? 'var(--font-mono)' : 'inherit',
                            whiteSpace: 'nowrap',
                            color: col.k === 'goal_differential'
                              ? (t.goal_differential > 0 ? 'var(--goal)'
                                : t.goal_differential < 0 ? 'var(--penalty)'
                                : 'var(--text-2)')
                              : 'var(--text)',
                          }}>
                            {renderCell(t, col)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tiebreaker footnote */}
      <div className="card" style={{ marginTop: 14, padding: 12, fontSize: 11, color: 'var(--text-3)' }}>
        <strong style={{ color: 'var(--text-2)' }}>Tiebreakers (in order):</strong>{' '}
        {data.tiebreakers.map((t, i) => (
          <span key={t}>
            {i > 0 && ' → '}
            {TIEBREAKER_OPTIONS.find(o => o.v === t)?.l || t}
          </span>
        ))}
      </div>
    </>
  );
}

function renderCell(team, col) {
  if (col.k === 'rank') {
    return team.rank;
  }
  if (col.k === 'team_name') {
    return team.team_name;
  }
  if (col.k === 'goal_differential') {
    const v = team.goal_differential;
    return v > 0 ? `+${v}` : String(v);
  }
  return team[col.k] ?? 0;
}

// ═══════════════════════════════════════════════════════════════
// LEADERBOARD TAB
// ═══════════════════════════════════════════════════════════════

const LEADERBOARD_COLS = [
  { k: 'rank',          l: '#',    a: 'left',   numeric: false, sortable: false },
  { k: 'display_name',  l: 'Player', a: 'left', numeric: false, sortable: false },
  { k: 'team_name',     l: 'Team',  a: 'left',  numeric: false, sortable: false },
  { k: 'games_played',  l: 'GP',   a: 'right',  numeric: true,  sortable: true },
  { k: 'goals',         l: 'G',    a: 'right',  numeric: true,  sortable: true },
  { k: 'assists',       l: 'A',    a: 'right',  numeric: true,  sortable: true },
  { k: 'points',        l: 'PTS',  a: 'right',  numeric: true,  sortable: true, bold: true },
  { k: 'pim',           l: 'PIM',  a: 'right',  numeric: true,  sortable: true },
];

function LeaderboardTab({ leagueId, seasonId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  // 2026-09-28 — playoff games no longer count toward the regular-season
  // leaders; 'playoffs' and 'all' are their own views.
  const [scope, setScope] = useState('regular');

  async function load(nextScope = scope) {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/leagues/${leagueId}/seasons/${seasonId}/leaderboard?scope=${nextScope}`);
      setData(res);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load leaderboard');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { setScope('regular'); load('regular'); /* eslint-disable-next-line */ }, [seasonId]);
  const pickScope = (sc) => { setScope(sc); load(sc); };
  const scopeBar = data?.has_playoffs ? (
    <div role="group" aria-label="Games counted" style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      {[['regular', 'Regular season'], ['playoffs', 'Playoffs'], ['all', 'All games']].map(([id, text]) => (
        <button key={id} type="button" onClick={() => pickScope(id)} aria-pressed={scope === id} style={{
          border: 'none', padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
          background: scope === id ? 'var(--ice-a10)' : 'transparent',
          color: scope === id ? 'var(--ice)' : 'var(--text-2)',
        }}>{text}</button>
      ))}
    </div>
  ) : null;

  if (loading && !data) return <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>;
  if (!data || data.players.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 36 }}>
        <Award size={36} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No player scoring yet</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
          Player rankings appear once games finalize with recorded goals.
        </div>
        {scopeBar && <div style={{ marginTop: 14 }}>{scopeBar}</div>}
      </div>
    );
  }

  function clickSort(col) {
    if (!col.sortable) return;
    if (sortKey === col.k) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortKey(null); setSortDir('desc'); }
    } else {
      setSortKey(col.k);
      setSortDir('desc');
    }
  }

  const players = sortKey
    ? data.players.slice().sort((a, b) => {
        const x = a[sortKey] ?? 0; const y = b[sortKey] ?? 0;
        return sortDir === 'desc' ? y - x : x - y;
      })
    : data.players;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--surface-2)', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          Top {data.players.length} scorers
          {data.scope === 'playoffs' ? ' · playoffs' : data.scope === 'all' ? ' · all games' : data.has_playoffs ? ' · regular season' : ''}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {scopeBar}
          <button type="button" className="btn-ghost" style={tinyBtn} onClick={() => load()}>
            <RefreshCw size={11} style={{ marginRight: 4 }} />
            Refresh
          </button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 640 }}>
          <thead>
            <tr>
              {LEADERBOARD_COLS.map(col => (
                <th key={col.k} onClick={() => clickSort(col)} style={{
                  textAlign: col.a,
                  padding: '8px 10px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 10, color: 'var(--text-2)',
                  textTransform: 'uppercase', letterSpacing: 0.6,
                  fontWeight: 700,
                  cursor: col.sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}>
                  {col.l}
                  {col.sortable && sortKey === col.k && (
                    sortDir === 'desc'
                      ? <ArrowDown size={9} style={{ marginLeft: 3, verticalAlign: 'middle' }} />
                      : <ArrowUp   size={9} style={{ marginLeft: 3, verticalAlign: 'middle' }} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.map((p, i) => (
              <tr key={p.profile_id} style={{
                background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
              }}>
                {LEADERBOARD_COLS.map(col => (
                  <td key={col.k} style={{
                    textAlign: col.a,
                    padding: '8px 10px',
                    borderBottom: i === players.length - 1 ? 'none' : '1px solid var(--border)',
                    fontWeight: col.bold ? 700 : 500,
                    fontFamily: col.numeric ? 'var(--font-mono)' : 'inherit',
                    whiteSpace: 'nowrap',
                  }}>
                    {col.k === 'rank' ? p.rank
                      : col.k === 'display_name' ? (
                          <>
                            {p.jersey_number != null && (
                              <span style={{
                                display: 'inline-block', minWidth: 24,
                                marginRight: 6, padding: '1px 5px',
                                background: 'var(--ice-a10)', color: 'var(--ice)',
                                borderRadius: 4, fontSize: 10, fontWeight: 800,
                                fontFamily: 'var(--font-mono)', textAlign: 'center',
                              }}>{p.jersey_number}</span>
                            )}
                            {p.display_name}
                          </>
                        )
                      : col.k === 'team_name' ? (p.team_name || '—')
                      : (p[col.k] ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCORING RULES EDITOR
// ═══════════════════════════════════════════════════════════════

const PRESET_RUBRICS = {
  classic: { win_reg: 2, win_ot: 2, win_so: 2, loss_ot: 1, loss_so: 1, tie: 1, loss_reg: 0 },
  modern:  { win_reg: 3, win_ot: 2, win_so: 2, loss_ot: 1, loss_so: 1, tie: 0, loss_reg: 0 },
  cup:     { win_reg: 1, win_ot: 1, win_so: 1, loss_ot: 0, loss_so: 0, tie: 0, loss_reg: 0 },
};

function ScoringRulesEditor({ leagueId, seasonId, initialRubric, initialTiebreakers, onSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [rubric, setRubric] = useState(initialRubric || PRESET_RUBRICS.classic);
  const [tiebreakers, setTiebreakers] = useState(initialTiebreakers ||
    ['points', 'wins', 'goal_differential', 'goals_for', 'head_to_head']);
  const [saving, setSaving] = useState(false);

  // Reset local state if a different season is selected upstream.
  useEffect(() => {
    setRubric(initialRubric || PRESET_RUBRICS.classic);
    setTiebreakers(initialTiebreakers || ['points', 'wins', 'goal_differential', 'goals_for', 'head_to_head']);
  }, [initialRubric, initialTiebreakers]);

  function setRubricField(k, v) {
    const n = parseInt(v, 10);
    if (Number.isInteger(n) && n >= 0 && n <= 10) {
      setRubric(r => ({ ...r, [k]: n }));
    } else if (v === '') {
      setRubric(r => ({ ...r, [k]: 0 }));
    }
  }
  function applyPreset(name) {
    setRubric(PRESET_RUBRICS[name]);
  }
  function moveTiebreaker(idx, delta) {
    setTiebreakers(prev => {
      const next = prev.slice();
      const j = idx + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }
  function toggleTiebreaker(v) {
    setTiebreakers(prev => prev.includes(v) ? prev.filter(t => t !== v) : [...prev, v]);
  }

  async function save() {
    if (tiebreakers.length === 0) return toast.error('Pick at least one tiebreaker');
    setSaving(true);
    try {
      await api.patch(`/leagues/${leagueId}/seasons/${seasonId}`, {
        points_rubric_json: rubric,
        tiebreakers_json: tiebreakers,
      });
      toast.success('Scoring rules saved · standings will reflect on next refresh');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <button type="button" onClick={() => setExpanded(!expanded)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'transparent', border: 'none', cursor: 'pointer',
        padding: 0, color: 'var(--text)', textAlign: 'left',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Settings size={14} color="var(--text-2)" />
          <span style={{ fontSize: 13, fontWeight: 700 }}>Scoring rules</span>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            · Points rubric and tiebreakers
          </span>
        </div>
        {expanded ? <ChevronDown size={14} color="var(--text-2)" /> : <ChevronRight size={14} color="var(--text-2)" />}
      </button>

      {expanded && (
        <div style={{ marginTop: 14 }}>
          {/* Presets */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
              Presets
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <PresetBtn onClick={() => applyPreset('classic')}>Classic (2-1-0)</PresetBtn>
              <PresetBtn onClick={() => applyPreset('modern')}>Modern NHL (3-2-1-0)</PresetBtn>
              <PresetBtn onClick={() => applyPreset('cup')}>Cup (1-0)</PresetBtn>
            </div>
          </div>

          {/* Rubric grid */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
              Points per result
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 8,
            }}>
              {RUBRIC_FIELDS.map(f => (
                <div key={f.k} style={{
                  padding: 8, borderRadius: 8,
                  background: 'var(--surface-2)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ flex: 1, fontSize: 11, color: 'var(--text-2)' }}>{f.l}</span>
                  <input type="number" min={0} max={10}
                    value={rubric[f.k] ?? 0}
                    onChange={(e) => setRubricField(f.k, e.target.value)}
                    style={{
                      width: 48,
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 6, padding: '4px 6px',
                      color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-mono)',
                      textAlign: 'center', outline: 'none',
                    }} />
                </div>
              ))}
            </div>
          </div>

          {/* Tiebreaker ordering */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
              Tiebreaker order (drag up/down)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {tiebreakers.map((tb, idx) => (
                <div key={tb} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px',
                  background: 'var(--surface-2)', borderRadius: 8,
                  border: '1px solid var(--border)',
                }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-3)', minWidth: 16 }}>
                    {idx + 1}
                  </span>
                  <span style={{ flex: 1, fontSize: 13 }}>
                    {TIEBREAKER_OPTIONS.find(o => o.v === tb)?.l || tb}
                  </span>
                  <button type="button" className="btn-ghost"
                    onClick={() => moveTiebreaker(idx, -1)} disabled={idx === 0}
                    style={tinyBtn} title="Move up">
                    <ArrowUp size={11} />
                  </button>
                  <button type="button" className="btn-ghost"
                    onClick={() => moveTiebreaker(idx, 1)}
                    disabled={idx === tiebreakers.length - 1}
                    style={tinyBtn} title="Move down">
                    <ArrowDown size={11} />
                  </button>
                  <button type="button" className="btn-ghost"
                    onClick={() => toggleTiebreaker(tb)}
                    style={{ ...tinyBtn, color: 'var(--penalty)' }} title="Remove">
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Add a tiebreaker:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {TIEBREAKER_OPTIONS.filter(o => !tiebreakers.includes(o.v)).map(o => (
                  <button key={o.v} type="button"
                    onClick={() => toggleTiebreaker(o.v)}
                    style={{
                      fontSize: 11, padding: '4px 8px',
                      background: 'var(--surface-2)', border: '1px solid var(--border)',
                      borderRadius: 6, cursor: 'pointer',
                      color: 'var(--text-2)',
                    }}>
                    + {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ice" onClick={save} disabled={saving}
              style={{ fontSize: 13 }}>
              <Save size={12} style={{ marginRight: 6 }} />
              {saving ? 'Saving…' : 'Save scoring rules'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PresetBtn({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      fontSize: 11, padding: '4px 10px',
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 6, cursor: 'pointer',
      color: 'var(--text-2)',
    }}>
      {children}
    </button>
  );
}

// ─── Shared bits ──────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  outline: 'none',
};
const tinyBtn = { padding: '4px 10px', fontSize: 11, minHeight: 'auto' };
